/**
 * Session ID Management Hook
 *
 * Manages session ID for guest users' carts.
 * The session ID is stored in localStorage and sent via X-Session-ID header.
 *
 * NOTE: For improved robustness, consider implementing server-side session
 * handling with cookies. The current backend uses HttpContext.Session which
 * requires proper cookie configuration. This localStorage approach works
 * but has limitations (no server-side session validation).
 *
 * @see https://docs.microsoft.com/en-us/aspnet/core/fundamentals/app-state
 */

import { useCallback, useMemo } from 'react';

const SESSION_STORAGE_KEY = 'stork_session_id';
const SESSION_EXPIRY_KEY = 'stork_session_expiry';
const SESSION_EXPIRY_DAYS = 7;

/**
 * Generates a UUID v4 for session identification
 */
const generateSessionId = (): string => {
    return crypto.randomUUID();
};

/**
 * Checks if the session has expired
 */
const isSessionExpired = (): boolean => {
    const expiryStr = localStorage.getItem(SESSION_EXPIRY_KEY);
    if (!expiryStr) return true;

    const expiry = new Date(expiryStr);
    return new Date() > expiry;
};

/**
 * Updates the session expiry timestamp
 */
const updateSessionExpiry = (): void => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + SESSION_EXPIRY_DAYS);
    localStorage.setItem(SESSION_EXPIRY_KEY, expiry.toISOString());
};

/**
 * Hook for managing session ID for guest users
 *
 * @returns Session management utilities
 */
export const useSession = () => {
    /**
     * Gets or creates a session ID
     * If the session has expired, generates a new one
     */
    const getSessionId = useCallback((): string => {
        let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

        // Generate new session if none exists or expired
        if (!sessionId || isSessionExpired()) {
            sessionId = generateSessionId();
            localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        }

        // Always refresh expiry on access
        updateSessionExpiry();

        return sessionId;
    }, []);

    /**
     * Clears the current session (useful after login when cart is merged)
     */
    const clearSession = useCallback((): void => {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        localStorage.removeItem(SESSION_EXPIRY_KEY);
    }, []);

    /**
     * Gets session ID without creating a new one
     * Returns null if no session exists
     */
    const peekSessionId = useCallback((): string | null => {
        const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
        if (!sessionId || isSessionExpired()) {
            return null;
        }
        return sessionId;
    }, []);

    /**
     * The current session ID (memoized)
     */
    const sessionId = useMemo(() => getSessionId(), [getSessionId]);

    return {
        sessionId,
        getSessionId,
        clearSession,
        peekSessionId,
    };
};

/**
 * Standalone function to get session ID (for use in API services)
 * This can be used outside of React components
 */
export const getSessionId = (): string => {
    let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

    if (!sessionId || isSessionExpired()) {
        sessionId = generateSessionId();
        localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }

    updateSessionExpiry();
    return sessionId;
};

/**
 * Explicitly sets a session ID (used when backend generates one)
 * This allows the backend to be the source of truth for session IDs
 */
export const setSessionId = (sessionId: string): void => {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    updateSessionExpiry();
};

/**
 * Standalone function to clear session (for use in API services)
 */
export const clearSessionId = (): void => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(SESSION_EXPIRY_KEY);
};

export default useSession;
